import { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  FormControlLabel,
  MenuItem,
  Paper,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import type {
  PublishResultsView,
  SessionExportView,
  ResultBatchView,
  ResultView,
  SessionProtocolView,
  SessionView,
  SupportedLocale,
} from '@stm/contracts';
import { translate } from '@stm/i18n';
import { ApiRequestError, apiRequest } from './api';
import {
  applyOutcomes,
  attemptsOf,
  changedRows,
  entryRowsFor,
  isDirty,
  localizedText,
  pendingEntryCount,
  resultItemOf,
  sessionExportCsv,
  type EntryRow,
} from './assessments';
import { athleteDisplayName, formatCalendarDate } from './athletes';

type ErrorKey = ApiRequestError['messageKey'];

function errorKeyOf(error: unknown): ErrorKey {
  return error instanceof ApiRequestError ? error.messageKey : 'error.unexpected';
}

export interface SessionEntryProps {
  locale: SupportedLocale;
  base: string;
  csrfToken: string;
  sessionId: string;
  onClose: () => void;
  onChanged: () => Promise<void>;
}

const sameRow = (row: EntryRow, athleteId: string, versionId: string): boolean =>
  row.athleteId === athleteId && row.definitionVersionId === versionId;

interface AttemptInputsProps {
  locale: SupportedLocale;
  protocol: SessionProtocolView;
  row: EntryRow;
  disabled: boolean;
  onChange: (row: EntryRow) => void;
}

/** The attempts of one athlete: a number, a yes/no choice or a rubric level, plus "invalid". */
function AttemptInputs({
  locale,
  protocol,
  row,
  disabled,
  onChange,
}: AttemptInputsProps): React.JSX.Element {
  const setCell = (index: number, changes: Partial<EntryRow['cells'][number]>): void => {
    onChange({
      ...row,
      conflict: false,
      problems: [],
      cells: row.cells.map((cell, position) =>
        position === index ? { ...cell, ...changes } : cell,
      ),
    });
  };
  return (
    <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center' }}>
      {row.cells.map((cell, index) => {
        const label = `${translate(locale, 'assessments.entry.attempt')} ${String(index + 1)}`;
        return (
          // The attempts are a fixed, ordered list, so the position is their identity.
          <Box key={`attempt-${String(index)}`} sx={{ display: 'flex', alignItems: 'center' }}>
            {protocol.valueType === 'boolean' || protocol.valueType === 'rubric' ? (
              <TextField
                select
                size="small"
                sx={{ minWidth: 130 }}
                disabled={disabled || cell.invalid}
                label={label}
                value={cell.value}
                onChange={(event) => {
                  setCell(index, { value: event.target.value });
                }}
              >
                <MenuItem value="">{translate(locale, 'assessments.entry.choose')}</MenuItem>
                {protocol.valueType === 'boolean'
                  ? [
                      <MenuItem key="1" value="1">
                        {translate(locale, 'assessments.entry.yes')}
                      </MenuItem>,
                      <MenuItem key="0" value="0">
                        {translate(locale, 'assessments.entry.no')}
                      </MenuItem>,
                    ]
                  : (protocol.rubricLevels ?? []).map((level) => (
                      <MenuItem key={level.level} value={String(level.level)}>
                        {`${translate(locale, 'assessments.entry.level')} ${String(level.level)} – ${localizedText(level.descriptions, locale)}`}
                      </MenuItem>
                    ))}
              </TextField>
            ) : (
              <TextField
                size="small"
                sx={{ width: 110 }}
                disabled={disabled || cell.invalid}
                label={label}
                value={cell.value}
                onChange={(event) => {
                  setCell(index, { value: event.target.value });
                }}
                slotProps={{
                  htmlInput: {
                    inputMode: protocol.precision === 0 ? 'numeric' : 'decimal',
                    'aria-label': `${label}${protocol.unit === null ? '' : ` (${protocol.unit})`}`,
                  },
                }}
              />
            )}
            <FormControlLabel
              sx={{ ml: 0.5 }}
              control={
                <Checkbox
                  size="small"
                  disabled={disabled}
                  checked={cell.invalid}
                  onChange={(event) => {
                    setCell(index, { invalid: event.target.checked, value: '' });
                  }}
                />
              }
              label={translate(locale, 'assessments.entry.invalid')}
            />
          </Box>
        );
      })}
    </Box>
  );
}

/**
 * The result entry sheet of one assessment session. Choices stay on the sheet until saved, with a
 * visible unsaved indicator, and a failed save keeps them. A row another coach changed first comes
 * back showing their entry instead of being overwritten, a published result is changed only through a
 * correction that says why, and drafts stay private until the coach publishes them.
 */
export function SessionEntry({
  locale,
  base,
  csrfToken,
  sessionId,
  onClose,
  onChanged,
}: SessionEntryProps): React.JSX.Element {
  const [session, setSession] = useState<SessionView | null>(null);
  const [rows, setRows] = useState<EntryRow[]>([]);
  const [protocolId, setProtocolId] = useState<string>('');
  const [errorKey, setErrorKey] = useState<ErrorKey | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [correcting, setCorrecting] = useState<{ athleteId: string; reason: string } | null>(null);
  const sessionPath = `${base}/assessment-sessions/${sessionId}`;

  const load = useCallback(async () => {
    try {
      const loaded = await apiRequest<SessionView>(sessionPath);
      setSession(loaded);
      setRows(loaded.protocols.flatMap((protocol) => entryRowsFor(loaded, protocol)));
      setProtocolId((current) =>
        loaded.protocols.some((protocol) => protocol.definitionVersionId === current)
          ? current
          : (loaded.protocols[0]?.definitionVersionId ?? ''),
      );
    } catch (error) {
      setErrorKey(errorKeyOf(error));
    }
  }, [sessionPath]);

  useEffect(() => {
    void load();
  }, [load]);

  if (session === null) {
    return errorKey === null ? (
      <Typography sx={{ mt: 2 }}>{translate(locale, 'assessments.loading')}</Typography>
    ) : (
      <Alert severity="error" sx={{ mt: 2 }}>
        {translate(locale, errorKey)}
      </Alert>
    );
  }

  const protocol =
    session.protocols.find((candidate) => candidate.definitionVersionId === protocolId) ??
    session.protocols[0];
  const shownRows = rows.filter((row) => row.definitionVersionId === protocol?.definitionVersionId);
  const pending = pendingEntryCount(rows);
  const aggregate = session.aggregates.find(
    (candidate) => candidate.definitionVersionId === protocol?.definitionVersionId,
  );
  const resultOf = (row: EntryRow): ResultView | undefined =>
    session.results.find(
      (candidate) =>
        candidate.athleteId === row.athleteId &&
        candidate.definitionVersionId === row.definitionVersionId,
    );

  function replaceRow(changed: EntryRow): void {
    setNotice(null);
    setRows((current) =>
      current.map((row) =>
        sameRow(row, changed.athleteId, changed.definitionVersionId) ? changed : row,
      ),
    );
  }

  async function save(): Promise<void> {
    if (protocol === undefined) return;
    setErrorKey(null);
    const items = changedRows(rows).map(resultItemOf);
    if (items.length === 0) return;
    try {
      const batch = await apiRequest<ResultBatchView>(`${sessionPath}/results`, {
        method: 'PATCH',
        body: { items },
        csrfToken,
      });
      const protocolsById = new Map(
        session?.protocols.map((candidate) => [candidate.definitionVersionId, candidate]),
      );
      setRows((current) =>
        current.map((row) => {
          const own = protocolsById.get(row.definitionVersionId);
          return own === undefined ? row : (applyOutcomes([row], batch, own)[0] ?? row);
        }),
      );
      setNotice(`${String(batch.saved)} ${translate(locale, 'assessments.entry.savedRows')}`);
      await onChanged();
    } catch (error) {
      setErrorKey(errorKeyOf(error));
    }
  }

  async function publish(): Promise<void> {
    setErrorKey(null);
    try {
      const outcome = await apiRequest<PublishResultsView>(`${sessionPath}/publish`, {
        method: 'POST',
        body: {},
        csrfToken,
      });
      await load();
      await onChanged();
      setNotice(
        `${String(outcome.published)} ${translate(locale, 'assessments.entry.publishDone')}`,
      );
    } catch (error) {
      setErrorKey(errorKeyOf(error));
    }
  }

  async function startCorrection(row: EntryRow, reason: string): Promise<void> {
    setErrorKey(null);
    const item = resultItemOf(row);
    try {
      await apiRequest<ResultView>(`${sessionPath}/corrections`, {
        method: 'POST',
        body: {
          athleteId: item.athleteId,
          definitionVersionId: item.definitionVersionId,
          correctionReason: reason.trim(),
          unit: null,
          attempts: item.attempts,
          selectedAttemptNumber: item.selectedAttemptNumber,
          selectionReason: item.selectionReason,
          privateNote: item.privateNote,
          publicComment: item.publicComment,
        },
        csrfToken,
      });
      setCorrecting(null);
      await load();
      await onChanged();
    } catch (error) {
      setErrorKey(errorKeyOf(error));
    }
  }

  async function exportResults(): Promise<void> {
    setErrorKey(null);
    try {
      const exported = await apiRequest<SessionExportView>(`${sessionPath}/export`);
      const url = URL.createObjectURL(
        new Blob([sessionExportCsv(exported, locale)], { type: 'text/csv;charset=utf-8' }),
      );
      const link = document.createElement('a');
      link.href = url;
      link.download = `assessment-results-${exported.sessionDate}.csv`;
      link.click();
      URL.revokeObjectURL(url);
    } catch (error) {
      setErrorKey(errorKeyOf(error));
    }
  }

  async function discardCorrection(row: EntryRow, version: number): Promise<void> {
    setErrorKey(null);
    try {
      await apiRequest<ResultView>(`${sessionPath}/corrections/discard`, {
        method: 'POST',
        body: {
          athleteId: row.athleteId,
          definitionVersionId: row.definitionVersionId,
          version,
        },
        csrfToken,
      });
      await load();
      await onChanged();
    } catch (error) {
      setErrorKey(errorKeyOf(error));
    }
  }

  const nameOf = (athleteId: string): string => {
    const participant = session.participants.find((candidate) => candidate.athleteId === athleteId);
    return participant === undefined ? '' : athleteDisplayName(participant, locale);
  };

  return (
    <Paper variant="outlined" sx={{ mt: 2, padding: { xs: 1.5, sm: 2 } }}>
      <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
        <Typography variant="h6" component="h3" sx={{ flexGrow: 1 }}>
          {formatCalendarDate(session.sessionDate, locale)}
          {session.context === null ? '' : ` · ${session.context}`}
        </Typography>
        {pending > 0 && (
          <Chip
            color="warning"
            label={`${translate(locale, 'assessments.entry.unsaved')} (${String(pending)})`}
          />
        )}
        <Button onClick={onClose}>{translate(locale, 'assessments.session.close')}</Button>
      </Box>
      {errorKey !== null && (
        <Alert severity="error" sx={{ my: 1 }}>
          {translate(locale, errorKey)}
        </Alert>
      )}
      {notice !== null && (
        <Alert severity="success" sx={{ my: 1 }}>
          {notice}
        </Alert>
      )}
      {protocol !== undefined && (
        <>
          <TextField
            select
            size="small"
            sx={{ mt: 1, maxWidth: '100%', minWidth: { sm: 260 } }}
            label={translate(locale, 'assessments.entry.protocol')}
            value={protocol.definitionVersionId}
            onChange={(event) => {
              setProtocolId(event.target.value);
            }}
          >
            {session.protocols.map((candidate) => (
              <MenuItem key={candidate.definitionVersionId} value={candidate.definitionVersionId}>
                {`${localizedText(candidate.titles, locale)} (${translate(locale, 'assessments.protocol.version')} ${String(candidate.versionNumber)})`}
              </MenuItem>
            ))}
          </TextField>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            {[
              protocol.protocol.distance,
              protocol.protocol.course,
              protocol.protocol.surface,
              protocol.protocol.method,
            ]
              .filter((part): part is string => part !== null)
              .join(' · ')}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            {translate(locale, 'assessments.entry.blankHint')}
          </Typography>
          <Stack spacing={1}>
            {shownRows.map((row) => {
              const result = resultOf(row);
              const correctionPending =
                (result?.published ?? null) !== null && (result?.draft ?? null) !== null;
              const isCorrecting = correcting?.athleteId === row.athleteId;
              const locked = row.published && !isCorrecting;
              const revision = result?.draft ?? result?.published ?? null;
              const counted = revision?.selectedValue ?? null;
              return (
                <Paper
                  key={row.athleteId}
                  variant="outlined"
                  sx={{ padding: 1.5, borderColor: row.conflict ? 'warning.main' : undefined }}
                >
                  <Box
                    sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap', mb: 1 }}
                  >
                    <Typography sx={{ fontWeight: 600, flexGrow: 1 }}>
                      {nameOf(row.athleteId)}
                    </Typography>
                    {row.published && (
                      <Chip
                        size="small"
                        color="success"
                        label={translate(locale, 'assessments.entry.publishedState')}
                      />
                    )}
                    {!row.published && row.existing && (
                      <Chip
                        size="small"
                        label={translate(locale, 'assessments.entry.draftState')}
                      />
                    )}
                    {!row.existing && (
                      <Chip
                        size="small"
                        variant="outlined"
                        label={translate(locale, 'assessments.entry.noResult')}
                      />
                    )}
                    {isDirty(row) && !locked && (
                      <Chip
                        size="small"
                        color="warning"
                        label={translate(locale, 'assessments.entry.unsaved')}
                      />
                    )}
                    {counted !== null && (
                      <Chip
                        size="small"
                        variant="outlined"
                        label={`${translate(locale, 'assessments.entry.counts')}: ${counted}${protocol.unit === null ? '' : ` ${protocol.unit}`}`}
                      />
                    )}
                  </Box>
                  <AttemptInputs
                    locale={locale}
                    protocol={protocol}
                    row={row}
                    disabled={locked}
                    onChange={replaceRow}
                  />
                  {protocol.aggregation === 'coach_selected' && (
                    <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mt: 1 }}>
                      <TextField
                        select
                        size="small"
                        sx={{ minWidth: 180 }}
                        disabled={locked}
                        label={translate(locale, 'assessments.entry.selectedAttempt')}
                        value={row.selectedAttempt}
                        onChange={(event) => {
                          replaceRow({ ...row, selectedAttempt: event.target.value, problems: [] });
                        }}
                      >
                        <MenuItem value="">
                          {translate(locale, 'assessments.entry.choose')}
                        </MenuItem>
                        {attemptsOf(row).map((attempt, index) =>
                          attempt.status === 'valid' ? (
                            <MenuItem key={`choice-${String(index)}`} value={String(index + 1)}>
                              {`${translate(locale, 'assessments.entry.attempt')} ${String(index + 1)}: ${attempt.value ?? ''}`}
                            </MenuItem>
                          ) : null,
                        )}
                      </TextField>
                      <TextField
                        size="small"
                        sx={{ minWidth: { xs: '100%', sm: 240 }, flexGrow: 1 }}
                        disabled={locked}
                        label={translate(locale, 'assessments.entry.reason')}
                        value={row.selectionReason}
                        onChange={(event) => {
                          replaceRow({ ...row, selectionReason: event.target.value, problems: [] });
                        }}
                      />
                    </Box>
                  )}
                  <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mt: 1 }}>
                    <TextField
                      size="small"
                      multiline
                      sx={{ minWidth: { xs: '100%', sm: 240 }, flexGrow: 1 }}
                      disabled={locked}
                      label={translate(locale, 'assessments.entry.privateNote')}
                      value={row.privateNote}
                      onChange={(event) => {
                        replaceRow({ ...row, privateNote: event.target.value });
                      }}
                    />
                    <TextField
                      size="small"
                      multiline
                      sx={{ minWidth: { xs: '100%', sm: 240 }, flexGrow: 1 }}
                      disabled={locked}
                      label={translate(locale, 'assessments.entry.publicComment')}
                      value={row.publicComment}
                      onChange={(event) => {
                        replaceRow({ ...row, publicComment: event.target.value });
                      }}
                    />
                  </Box>
                  {row.conflict && (
                    <Alert severity="warning" sx={{ mt: 1 }}>
                      {translate(locale, 'assessments.entry.conflict')}
                    </Alert>
                  )}
                  {row.problems.map((problem) => (
                    <Alert key={problem} severity="error" sx={{ mt: 1 }}>
                      {translate(locale, problem as ErrorKey)}
                    </Alert>
                  ))}
                  {correctionPending && (
                    <Alert severity="info" sx={{ mt: 1 }}>
                      {translate(locale, 'assessments.entry.correctionDraft')}
                      {result !== undefined && (
                        <Button
                          size="small"
                          sx={{ ml: 1 }}
                          onClick={() => {
                            void discardCorrection(row, result.version);
                          }}
                        >
                          {translate(locale, 'assessments.entry.discardCorrection')}
                        </Button>
                      )}
                    </Alert>
                  )}
                  {row.published && !isCorrecting && (
                    <Box
                      sx={{
                        mt: 1,
                        display: 'flex',
                        gap: 1,
                        alignItems: 'center',
                        flexWrap: 'wrap',
                      }}
                    >
                      <Typography variant="body2" color="text.secondary">
                        {translate(locale, 'assessments.entry.published')}
                      </Typography>
                      <Button
                        size="small"
                        variant="outlined"
                        onClick={() => {
                          setCorrecting({ athleteId: row.athleteId, reason: '' });
                        }}
                      >
                        {translate(locale, 'assessments.entry.correct')}
                      </Button>
                    </Box>
                  )}
                  {isCorrecting && (
                    <Box
                      component="form"
                      sx={{
                        mt: 1,
                        display: 'flex',
                        gap: 1,
                        flexWrap: 'wrap',
                        alignItems: 'flex-start',
                      }}
                      onSubmit={(event) => {
                        event.preventDefault();
                        void startCorrection(row, correcting.reason);
                      }}
                    >
                      <TextField
                        size="small"
                        required
                        sx={{ minWidth: { xs: '100%', sm: 260 }, flexGrow: 1 }}
                        label={translate(locale, 'assessments.entry.correctionReason')}
                        value={correcting.reason}
                        onChange={(event) => {
                          setCorrecting({ athleteId: row.athleteId, reason: event.target.value });
                        }}
                      />
                      <Button
                        type="submit"
                        variant="contained"
                        disabled={correcting.reason.trim() === '' || attemptsOf(row).length === 0}
                      >
                        {translate(locale, 'assessments.entry.startCorrection')}
                      </Button>
                      <Button
                        onClick={() => {
                          setCorrecting(null);
                          void load();
                        }}
                      >
                        {translate(locale, 'assessments.entry.cancelCorrection')}
                      </Button>
                    </Box>
                  )}
                </Paper>
              );
            })}
          </Stack>
          <Box sx={{ mt: 2, display: 'flex', gap: 1, flexWrap: 'wrap' }}>
            <Button
              variant="contained"
              sx={{ minHeight: 44 }}
              disabled={pending === 0}
              onClick={() => {
                void save();
              }}
            >
              {translate(locale, 'assessments.entry.save')}
            </Button>
            <Button
              variant="outlined"
              sx={{ minHeight: 44 }}
              disabled={pending > 0}
              onClick={() => {
                void publish();
              }}
            >
              {translate(locale, 'assessments.entry.publish')}
            </Button>
            <Button
              variant="outlined"
              sx={{ minHeight: 44 }}
              onClick={() => {
                void exportResults();
              }}
            >
              {translate(locale, 'assessments.export.button')}
            </Button>
          </Box>
          {aggregate !== undefined && (
            <Box
              component="section"
              sx={{ mt: 3 }}
              aria-label={translate(locale, 'assessments.aggregates.title')}
            >
              <Typography variant="subtitle1" component="h4" gutterBottom>
                {translate(locale, 'assessments.aggregates.title')}
              </Typography>
              <Typography variant="body2">
                {translate(locale, 'assessments.aggregates.count')}: {aggregate.count}
              </Typography>
              {aggregate.suppressed ? (
                <Typography variant="body2" color="text.secondary">
                  {translate(locale, 'assessments.aggregates.suppressed')}
                </Typography>
              ) : (
                <Typography variant="body2">
                  {translate(locale, 'assessments.aggregates.minimum')}: {aggregate.minimum} ·{' '}
                  {translate(locale, 'assessments.aggregates.maximum')}: {aggregate.maximum} ·{' '}
                  {translate(locale, 'assessments.aggregates.mean')}: {aggregate.mean}
                </Typography>
              )}
            </Box>
          )}
        </>
      )}
    </Paper>
  );
}
